import { format } from "date-fns";
import { Shield, ShieldOff, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SourceChip } from "@/components/Chips";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export const ScoreFactors = ({ factors }) => (
  <section data-testid="score-factors" className="rounded-2xl border glass p-5 sm:p-6">
    <div className="flex items-center justify-between gap-2">
      <h2 className="text-xl font-medium">Score factors</h2>
      <SourceChip label="Estimate" testid="score-factors-source" />
    </div>
    <ul className="mt-4 space-y-4">
      {factors.map((f) => (
        <li key={f.key} data-testid={`score-factor-${f.key}`}>
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">{f.label}</span>
            <span className="font-mono text-xs text-muted-foreground">{f.points}/{f.max}</span>
          </div>
          <div className="mt-1.5 h-1.5 rounded-full bg-secondary overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-[#8B7CFF] to-[#5EE6D0] shadow-[0_0_10px_rgba(94,230,208,0.5)] transition-[width] duration-700" style={{ width: `${(f.points / f.max) * 100}%` }} />
          </div>
          <p data-testid={`score-factor-reason-${f.key}`} className="mt-1.5 text-xs text-muted-foreground">{f.reason}</p>
        </li>
      ))}
    </ul>
  </section>
);

export const ShieldPanel = ({ listing, onUnlock, onLock }) => {
  if (!listing.bestseller) return null;
  return (
    <section data-testid="bestseller-shield-panel" className="rounded-2xl border border-[#E9C98B]/30 bg-[#E9C98B]/[0.06] p-5">
      <div className="flex items-start gap-3">
        {listing.shielded ? <Shield className="h-6 w-6 text-[#E9C98B]" /> : <ShieldOff className="h-6 w-6 text-[#E9C98B]" />}
        <div className="flex-1">
          <p data-testid="bestseller-shield-label" className="font-display text-lg text-[#F3DDAE]">
            {listing.shielded ? "Protected bestseller" : "Bestseller Shield unlocked"}
          </p>
          <p className="text-sm text-[#E9C98B]/75">
            {listing.sales_60d} sales in the last 60 days. {listing.shielded ? "Pushes are blocked until you unlock it." : "Pushes are allowed for this listing."}
          </p>
          {listing.shielded ? (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="outline" className="mt-3 rounded-full border-[#E9C98B]/35 bg-white/5" data-testid="shield-unlock-button">Unlock</Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="shield-unlock-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Unlock this bestseller?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This listing sold recently. Changing a bestseller can affect how it performs. You can still review every change and undo with one click.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="shield-unlock-cancel">Keep protected</AlertDialogCancel>
                  <AlertDialogAction onClick={onUnlock} data-testid="shield-unlock-confirm">Unlock</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : (
            <Button size="sm" variant="ghost" className="mt-3 rounded-full" onClick={onLock} data-testid="shield-relock-button">Re-lock</Button>
          )}
        </div>
      </div>
    </section>
  );
};

export const VersionHistory = ({ versions }) => (
  <section data-testid="version-history" className="rounded-2xl border glass p-5 sm:p-6">
    <h2 className="flex items-center gap-2 text-xl font-medium"><History className="h-5 w-5" />Version history</h2>
    {!versions.length ? (
      <p data-testid="version-history-empty" className="mt-3 text-sm text-muted-foreground">No changes pushed yet. The original listing is kept as a backup before any push.</p>
    ) : (
      <ol className="mt-4 space-y-3">
        {versions.map((v) => (
          <li key={v.id} data-testid={`version-entry-${v.id}`} data-action={v.action} className="rounded-xl bg-secondary/50 p-3 text-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-medium">{v.action === "undo" ? "Undo — restored previous version" : "Pushed fix (previous version saved)"}{v.undone ? " · undone" : ""}</span>
              <span className="font-mono text-xs text-muted-foreground">{format(new Date(v.created_at), "MMM d, yyyy HH:mm")}</span>
            </div>
            <p className="mt-1 text-xs text-muted-foreground" data-testid={`version-fields-${v.id}`}>Fields changed: {v.fields_changed.join(", ")}</p>
            {v.fields_changed.includes("title") && (
              <p className="mt-1 text-xs truncate"><span className="text-muted-foreground">Before:</span> {v.before.title}</p>
            )}
          </li>
        ))}
      </ol>
    )}
  </section>
);
